import { Button, Dialog, Popover, Slide } from "@material-ui/core";
import STBoardDetail from "../../assets/styles/organisms/boardDetail.module.scss";
import STGoodButtonAnimation from "../../assets/styles/atoms/goodButtonAnimation.module.scss";
import { useState, useEffect, ReactNode, forwardRef } from "react";
import { TransitionProps } from "@material-ui/core/transitions/transition";
import { useIsMobile } from "../../hooks/useIsMobile";
import { fetchBoardLikes } from "../../utils/api/fetchBoardLikes";
import { postBoardLikeStatus } from "../../utils/api/postBoardLikes";
import Avatar from "../atoms/Avatar";
import { useGetTabUUID } from "../organisms/comments/hooks";

type BoardLikesProps = {
  rootId: number;
  likeFlg: number;
  totalOfLikes: number;
  className?: string;
  likeType?: number;
  onSuccess?: () => void;
};

type LikeList = {
  uid: string;
  avatar_url: string;
  like_flg: number;
  nickname: string;
};

type LikesListProps = {
  children: ReactNode;
  likeId: string | undefined;
  openLikePopover: boolean;
  likeAnchorEl: HTMLButtonElement | null;
  className?: string;
  handleCloseLikeButton: () => void;
};

const Transition = forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const LikesList = (props: LikesListProps) => {
  const {
    children,
    likeId,
    openLikePopover,
    likeAnchorEl,
    handleCloseLikeButton,
  } = props;
  const { isMobile } = useIsMobile();
  if (isMobile) {
    return (
      <Dialog
        fullScreen
        open={openLikePopover}
        onClose={handleCloseLikeButton}
        TransitionComponent={Transition}
        classes={{
          root: STBoardDetail.customDialog,
        }}
        PaperProps={{
          style: {
            top: "60%",
            padding: 0,
          },
        }}
      >
        {children}
      </Dialog>
    );
  }
  return (
    <Popover
      id={likeId}
      open={openLikePopover}
      anchorEl={likeAnchorEl}
      onClose={handleCloseLikeButton}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      {children}
    </Popover>
  );
};

const BoardLikes = (props: BoardLikesProps) => {
  const {
    rootId = 0,
    likeFlg = false,
    likeType = 1,
    totalOfLikes = 0,
    className = "",
    onSuccess,
  } = props ?? {};
  const deviceId = useGetTabUUID();
  const [isLiked, setIsLiked] = useState<boolean>(!!likeFlg);
  const [totalLiked, setTotalLiked] = useState<number>(totalOfLikes);
  const [isClickedClass, setIsClickedClass] = useState(
    `${STGoodButtonAnimation.isClicked}`
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [likedList, setLikedList] = useState<LikeList[] | []>([]);
  const [showLikedDialog, setShowLikedDialog] = useState<boolean>(false);
  const handleToggleLikedDialog = () => {
    setShowLikedDialog((pre) => !pre);
  };

  const [likeAnchorEl, setLikeAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );
  const handleClickLikeButton = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    if (!!totalLiked) setLikeAnchorEl(event.currentTarget);
  };
  const handleCloseLikeButton = () => {
    setLikeAnchorEl(null);
  };
  const openLikePopover = Boolean(likeAnchorEl) && !!totalLiked;
  const likeId = likeAnchorEl ? "like-popover" : undefined;

  useEffect(() => {
    setTotalLiked(totalOfLikes);
    setIsLiked(!!likeFlg);
    const buttonClass = !!likeFlg
      ? `${STGoodButtonAnimation.likeButton} ${STGoodButtonAnimation.clicked}`
      : `${STGoodButtonAnimation.likeButton}`;
    setIsClickedClass(buttonClass);
  }, [likeFlg, totalOfLikes]);

  useEffect(() => {
    if (showLikedDialog && rootId && !!totalLiked) {
      const getBoardLikes = async () => {
        if (!showLikedDialog || !rootId) {
          return;
        }
        setIsLoading(true);
        try {
          const response = await fetchBoardLikes(rootId, likeType);
          if (response.isSuccess) {
            const result = response?.result?.data ?? [];
            setLikedList(result);
          } else {
            throw new Error(response?.message);
          }
        } catch (error) {
          console.log("error", error);
        } finally {
          setIsLoading(false);
        }
      };
      getBoardLikes();
    }
  }, [likeType, rootId, showLikedDialog, totalLiked]);

  useEffect(() => {
    const buttonClass = !!isLiked
      ? `${STGoodButtonAnimation.likeButton} ${STGoodButtonAnimation.clicked}`
      : `${STGoodButtonAnimation.likeButton}`;
    setIsClickedClass(buttonClass);
  }, [isLiked]);

  const handleClickButton = async () => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const newStatus = Number(!isLiked);
      const response = await postBoardLikeStatus({
        like_type: likeType,
        root_id: rootId,
        status: newStatus,
        device_id: deviceId,
      });
      if (response.isSuccess) {
        setIsLiked((prev) => !prev);
        setTotalLiked((prevTotal) => {
          return newStatus ? prevTotal + 1 : Math.max(prevTotal - 1, 0);
        });
        const buttonClass = !isLiked
          ? `${STGoodButtonAnimation.likeButton} ${STGoodButtonAnimation.clicked}`
          : `${STGoodButtonAnimation.likeButton}`;
        setIsClickedClass(buttonClass);
        if (onSuccess) onSuccess();
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`${STBoardDetail.boardFooterLikes} ${className}`}>
      <button style={{ width: 26, height: 37 }} onClick={handleClickButton}>
        <svg
          className={isClickedClass}
          width="36px"
          height="36px"
          viewBox="0 0 500 500"
        >
          <circle
            className={STGoodButtonAnimation.explosion}
            r="150"
            cx="250"
            cy="250"
          ></circle>
          <g className={STGoodButtonAnimation.particleLayer}>
            <circle fill="#8CE8C3" cx="130" cy="126.5" r="12.5" />
            <circle fill="#8CE8C3" cx="411" cy="313.5" r="12.5" />
            <circle fill="#91D2FA" cx="279" cy="86.5" r="12.5" />
            <circle fill="#91D2FA" cx="155" cy="390.5" r="12.5" />
            <circle fill="#CC8EF5" cx="89" cy="292.5" r="10.5" />
            <circle fill="#9BDFBA" cx="414" cy="282.5" r="10.5" />
            <circle fill="#9BDFBA" cx="115" cy="149.5" r="10.5" />
            <circle fill="#9FC7FA" cx="250" cy="80.5" r="10.5" />
            <circle fill="#9FC7FA" cx="78" cy="261.5" r="10.5" />
            <circle fill="#96D8E9" cx="182" cy="402.5" r="10.5" />
            <circle fill="#CC8EF5" cx="401.5" cy="166" r="13" />
            <circle fill="#DB92D0" cx="379" cy="141.5" r="10.5" />
            <circle fill="#DB92D0" cx="327" cy="397.5" r="10.5" />
            <circle fill="#DD99B8" cx="296" cy="392.5" r="10.5" />
          </g>
          <path
            className={STGoodButtonAnimation.heart}
            d="M250,187.4c-31.8-47.8-95.5-19.8-95.5,32.2c0,35.2,31.8,60.3,55.7,79.2c24.9,19.7,31.8,23.9,39.8,31.8 c7.9-7.9,14.6-12.6,39.8-31.8c24.3-18.5,55.7-44.4,55.7-79.6C345.5,167.6,281.8,139.7,250,187.4z"
          />
        </svg>
      </button>

      <Button
        style={{
          padding: 0,
          width: "fit-content",
          minWidth: "fit-content",
        }}
        aria-describedby={likeId}
        onClick={handleClickLikeButton}
      >
        <span onClick={handleToggleLikedDialog}>{totalLiked}</span>
      </Button>
      <LikesList
        openLikePopover={openLikePopover}
        likeId={likeId}
        likeAnchorEl={likeAnchorEl}
        handleCloseLikeButton={handleCloseLikeButton}
      >
        <div onClick={handleCloseLikeButton}>
          {isLoading ? (
            <></>
          ) : (
            <div>
              <div className={STBoardDetail.totalLikes}>
                <p>{`いいね（${likedList.length})`}</p>
              </div>
              <div className={STBoardDetail.likesContainer}>
                {likedList?.map((e, index) => {
                  const { avatar_url = "", nickname = "" } = e ?? {};
                  return (
                    <div
                      key={`liked_list_${rootId}_${likeType}_${index}`}
                      className={STBoardDetail.likedItem}
                    >
                      <Avatar
                        avatarUrl={avatar_url}
                        className={STBoardDetail.likedAvatar}
                      />
                      <div className={STBoardDetail.likedNickname}>
                        {nickname}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </LikesList>
    </div>
  );
};

export default BoardLikes;
